import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  Checkbox,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Flex,
} from '@chakra-ui/react';
import {
  eliminateHtmlTags,
  Loader,
  useToastHook,
} from '@devd-client/devd/components';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { QUERY_KEYS, useGoalSettingsMutation } from '../../apis';
import { FieldType, GoalDto, GoalDtoWithTeam } from '../../helpers/goals.types';

interface SettingsModalProps {
  handleModalClose: () => void;
  isOpen: boolean;
  selectedTeam: string;
  clickedGoal: GoalDto | null;
}

const TopContainer = styled.div``;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 40%;
  margin-bottom: 20px;
`;

const InputLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 15px;
  margin-bottom: 4px;
`;

const SettingsModal = ({
  handleModalClose,
  isOpen,
  selectedTeam,
  clickedGoal,
}: SettingsModalProps) => {
  const [newToast] = useToastHook();
  const queryClient = useQueryClient();
  const { mutate: handleGoalUpdate, isLoading } = useGoalSettingsMutation();

  const [dataToSend, setDataToSend] = useState<GoalDtoWithTeam | undefined>(
    undefined
  );

  useEffect(() => {
    if (clickedGoal) {
      setDataToSend({ ...clickedGoal, team: selectedTeam });
    }
  }, [clickedGoal, selectedTeam]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          handleModalClose();
        }}
        isCentered
        motionPreset="slideInBottom"
        size="lg"
      >
        <ModalOverlay />
        <ModalContent rounded="md">
          <ModalHeader>
            <Box
              as="h2"
              color="text.primary"
              fontFamily="heading"
              fontSize="md"
              lineHeight="20px"
              fontWeight="bold"
            >
              {'Settings'}
            </Box>
          </ModalHeader>
          <ModalCloseButton color="text.secondary" fontSize="xs" />

          <ModalBody display="flex" flexDirection="column">
            <TopContainer>
              {clickedGoal?.fields?.map((fieldItem: FieldType, idx: number) => (
                <InputGroup key={`field-${idx}`}>
                  {fieldItem?.type !== 'Boolean' && (
                    <InputLabel>{fieldItem.field}</InputLabel>
                  )}

                  {fieldItem.type === 'Number' ? (
                    <NumberInput
                      defaultValue={
                        Object.entries(clickedGoal.fieldsValue)
                          .filter(([k, v]) => k === fieldItem.key)
                          .map(([k, v]) => v)[0] as string
                      }
                      min={0}
                      onChange={(numberValue: string) => {
                        if (!dataToSend) return;
                        setDataToSend({
                          ...dataToSend,
                          fieldsValue: {
                            ...dataToSend.fieldsValue,
                            [fieldItem.key]: eliminateHtmlTags(numberValue),
                          },
                        });
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  ) : fieldItem.type === 'Text' ? (
                    <Input
                      defaultValue={
                        Object.entries(clickedGoal.fieldsValue)
                          .filter(([k, v]) => k === fieldItem.key)
                          .map(([k, v]) => v)[0] as string
                      }
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        const validKeys = /^[a-zA-Z_-]$/;
                        const specialKeys = [
                          'Backspace',
                          'Tab',
                          'ArrowLeft',
                          'ArrowRight',
                          'Delete',
                        ];

                        if (
                          !validKeys.test(e.key) &&
                          !specialKeys.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e: any) => {
                        if (!dataToSend) return;
                        setDataToSend({
                          ...dataToSend,
                          fieldsValue: {
                            ...dataToSend.fieldsValue,
                            [fieldItem.key]: eliminateHtmlTags(e.target.value),
                          },
                        });
                      }}
                    />
                  ) : fieldItem.type === 'Boolean' ? (
                    <Flex gap={2} alignItems="center">
                      <Checkbox
                        defaultChecked={
                          Object.entries(clickedGoal.fieldsValue)
                            .filter(([k, v]) => k === fieldItem.key)
                            .map(([k, v]) => Boolean(v))[0]
                        }
                        onChange={(e) => {
                          if (!dataToSend) return;
                          setDataToSend({
                            ...dataToSend,
                            fieldsValue: {
                              ...dataToSend.fieldsValue,
                              [fieldItem.key]: String(e.target.checked),
                            },
                          });
                        }}
                        mb={'4px'}
                      />
                      <InputLabel>{fieldItem.field}</InputLabel>
                    </Flex>
                  ) : (
                    <></>
                  )}
                </InputGroup>
              ))}
            </TopContainer>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="filled"
              onClick={() => {
                handleGoalUpdate(dataToSend, {
                  onSuccess: () => {
                    queryClient.invalidateQueries([QUERY_KEYS.goals]);
                    newToast({
                      message: `Agreement settings updated successfully.`,
                      status: 'success',
                    });

                    setTimeout(() => {
                      handleModalClose();
                    }, 500);
                  },
                });
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {isLoading && <Loader />}
    </>
  );
};

export default React.memo(SettingsModal);
